import React, { ReactElement } from 'react';
import { Field } from 'redux-form/immutable';
import { Button, FormControl, Grid, InputLabel, MenuItem } from '@material-ui/core';
import DeleteIcon from '@mui/icons-material/Delete';

import DonationCreditRuleType from 'entities/donationCreditRule/model';
import FormTextField from 'retired/FormTextField';
import * as validators from 'utils/validators';
import { currencies } from 'utils/currencyFormat';
import DialogButton from 'retired/DialogButton';
import FormSelect from 'retired/FormSelect';
import FormCheckbox from 'retired/FormCheckbox';
import { TotalLimitLabel } from 'containers/DonationRulesListPage/TotalLimitLabel';

const required = validators.required();
const CurrencyCodeSelector = ({ budget, ...rest }: { budget: any; rest: any }): ReactElement => (
  <FormSelect displayEmpty {...rest}>
    {[{ currencyCode: '', currency: { label: 'Select a currency' } }]
      .concat(
        Object.keys(currencies).map((currencyCode) => ({
          currencyCode,
          currency: currencies[currencyCode],
        }))
      )
      .map(({ currencyCode, currency }) => (
        <MenuItem
          key={currencyCode}
          value={currencyCode}
          disabled={budget?.currencyCode !== currencyCode}
        >
          {currency.label}
        </MenuItem>
      ))}
  </FormSelect>
);
const DonationCreditRule = ({
  donationCreditRule,
  budget,
  pristine,
  invalid,
  donationCreditRuleId,
  remove,
}: {
  donationCreditRule: DonationCreditRuleType;
  budget: any;
  pristine: boolean;
  invalid: boolean;
  remove: () => void;
  donationCreditRuleId: string;
}): ReactElement => {
  const selectedCurrencyCode =
    donationCreditRule?.get('currencyCode') || budget.currencyCode || 'USD';
  const selectedCurrency = currencies[selectedCurrencyCode];

  const validateTotalLimit = (value: string, rule: DonationCreditRuleType) => {
    if (value !== '') {
      if (Number(value) <= 0) {
        return 'Spending limit must be greater than or equal to 0';
      }

      if (rule.get('totalIssued') > Number(value)) {
        return 'You can not set the spending limit to less than what has already been spent';
      }

      if (Number.isFinite(budget?.limitTotal) && budget?.limitTotal < Number(value)) {
        return 'You can not set the spending limit to more than the parent budget';
      }
    }

    return undefined;
  };

  return (
    <div style={{ border: '1px solid rgba(0, 0, 0, 0.2)', padding: 30, marginBottom: 30 }}>
      <Field name="type" component="input" type="hidden" value="DonationCreditRule" />
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
        <div style={{ flexGrow: 1 }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Rule Name</InputLabel>
          </FormControl>
          <FormControl style={{ width: '100%' }}>
            <Field
              name="title"
              component={FormTextField}
              placeholder="Rule Name"
              validate={required}
              required
              alwaysDisplayErrors
            />
          </FormControl>
        </div>
        {donationCreditRuleId !== 'new' && !donationCreditRule?.get('donationsCreditCount') && (
          <DialogButton
            style={{ display: 'inline-block' }}
            title="Remove Rule"
            text="Are you sure you want to remove this Donation Credit Rule?"
            confirmLabel="Remove"
            cancelLabel="Cancel"
            iconButton
            handleConfirm={remove}
          >
            <DeleteIcon />
          </DialogButton>
        )}
      </div>
      <div style={{ marginBottom: 10 }}>
        <Grid container spacing={2}>
          <Grid item md={6}>
            <Field
              name="currencyCode"
              component={CurrencyCodeSelector}
              validate={required}
              required
              type="string"
              budget={budget}
              defaultValue={selectedCurrencyCode}
              label="Rule Currency"
              labelProps={{ shrink: true }}
              formControlProps={{ style: { width: '70%', height: 17 } }}
            />
          </Grid>
          <Grid item md={6}>
            <TotalLimitLabel />
            {selectedCurrency.symbol}
            <Field
              name="limitTotal"
              component={FormTextField}
              placeholder="Total limit"
              max={Number.isFinite(budget?.limitTotal) ? budget.limitTotal : Infinity}
              min={0}
              type="number"
              validate={validateTotalLimit}
              alwaysDisplayErrors
            />
          </Grid>
        </Grid>
      </div>
      <div style={{ margin: '20px 0 0 0' }}>
        <Field
          name="bypassBudgetLimitPerUser"
          component={FormCheckbox}
          label="Do not apply individual budget cap to these funds"
        />
      </div>
      <div style={{ marginTop: 30 }}>
        <Button type="submit" disabled={invalid} color="primary" variant="contained">
          {pristine ? 'Close' : 'Submit'}
        </Button>
      </div>
    </div>
  );
};

export default DonationCreditRule;
